import React from 'react';
import Button from '@material-ui/core/Button';
import Dialog, { type DialogProps } from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormLabel from '@material-ui/core/FormLabel';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import { Controller, useForm } from 'react-hook-form';
import type { CodeDeliveryMethod, ISendPhoneVerificationCodeOptions } from '../../resources/Server';
import { TextInput } from '../generic';

interface EnterContactInfoDialogProps extends Omit<DialogProps, 'open' | 'onSubmit'> {
  isOpen: boolean;
  onSubmit: (data: ISendPhoneVerificationCodeOptions) => void;
}

export function EnterContactInfoDialog(props: EnterContactInfoDialogProps) {
  const { isOpen, onSubmit, ...rest } = props;

  const form = useForm<ISendPhoneVerificationCodeOptions>();

  function handleSubmit(data: ISendPhoneVerificationCodeOptions) {
    onSubmit({ ...data, phoneNumber: data.phoneNumber.replace(/[^\d+]/g, '') });
  }

  return (
    <Dialog {...rest} open={isOpen}>
      <form className="px-4 py-8 sm:px-10 sm:pt-11" onSubmit={form.handleSubmit(handleSubmit)}>
        <h2 className="text-gray-900 text-3xl m-0">Please verify your phone number</h2>

        <div className="mt-4 mb-8 flex flex-col gap-8 text-grey-900">
          <div>
            <p>To keep your transactions secure, we'll verify your phone before signing.</p>
            <p>Please enter your phone number below:</p>
          </div>

          <TextInput
            label="Phone Number"
            mask="+1 (999) 999-9999"
            autoComplete="tel"
            className="mb-2"
            inputProps={form.register('phoneNumber', {
              pattern: {
                message: 'Invalid phone number',
                value: /^\+1 \(\d{3}\) \d{3}-\d{4}$/,
              },
              required: 'Required',
            })}
            error={form.formState.errors.phoneNumber?.message}
          />

          <div>
            <Controller
              name="deliveryMethod"
              control={form.control}
              defaultValue="text"
              rules={{ required: 'Required' }}
              render={({ field }) => (
                <FormControl component="fieldset" className="group">
                  <FormLabel component="legend" className="text-grey-900 group-focus-within:text-blue-800">
                    Preferred Method of Contact
                  </FormLabel>
                  <RadioGroup {...field} aria-label="gender" name="gender1" color="primary" className="flex flex-row">
                    <FormControlLabel
                      value={'text' satisfies CodeDeliveryMethod}
                      control={<Radio color="primary" />}
                      label="Text Message"
                    />
                    <FormControlLabel
                      value={'voice' satisfies CodeDeliveryMethod}
                      control={<Radio color="primary" />}
                      label="Phone Call"
                    />
                  </RadioGroup>
                </FormControl>
              )}
            />
          </div>

          <div className="flex flex-col gap-1 text-xs">
            <p>
              When you enter your mobile number, you're giving SkySlope permission to send you text messages for phone
              verification. You'll get one message each time you verify. Need help? Call us at 1-800-507-4117. Standard
              message and data rates may apply. Text STOP at any time to opt out. &nbsp;
              <a href="https://www.skyslope.com/privacy-policy" target="_blank" style={{ color: 'inherit' }}>
                Privacy policy
              </a>
              &nbsp;
              <a href="https://www.skyslope.com/terms-conditions" target="_blank" style={{ color: 'inherit' }}>
                Terms and Conditions
              </a>
            </p>
          </div>
        </div>

        <DialogActions>
          <Button type="submit" color="primary" autoFocus variant="contained" size="medium">
            Continue
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
